import { motion } from "framer-motion";
import { Activity, Clock, Radio, Zap } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";

interface QuickStatsProps {
  currentStreak: number;
  todaysListening: number;
  discoveryRate: number;
  averageDaily: number;
}

export const QuickStats: React.FC<QuickStatsProps> = ({
  currentStreak,
  todaysListening,
  discoveryRate,
  averageDaily,
}) => {
  const stats = [
    {
      icon: Clock,
      label: "Current Streak",
      value: `${currentStreak} days`,
      color: "text-yellow-500",
    },
    {
      icon: Activity,
      label: "Today's Listening",
      value: `${Math.round(todaysListening)} min`,
      subtext:
        averageDaily > 0
          ? `${Math.round((todaysListening / averageDaily) * 100)}% of daily avg`
          : "No daily average data",
      color: "text-purple-500",
    },
    {
      icon: Radio,
      label: "Discovery Rate",
      value: `${Math.round(discoveryRate * 100)}%`,
      subtext: "New artists this month",
      color: "text-blue-500",
    },
    {
      icon: Zap,
      label: "Daily Average",
      value: `${Math.round(averageDaily)} min`,
      color: "text-amber-500",
    },
  ];

  return (
    <Link to="/statistics" className="block">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-xl bg-zinc-900/50 backdrop-blur-sm border border-white/5 hover:bg-zinc-900/60 transition-colors group"
      >
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-zinc-800/50 to-zinc-900/50" />
          <div className="absolute top-0 right-0 w-64 h-64 bg-yellow-500/5 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2 group-hover:bg-yellow-500/10 transition-colors" />
        </div>

        {/* Content */}
        <div className="relative p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="space-y-2"
              >
                <div className={`w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center ${stat.color}`}>
                  <stat.icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xl font-bold">{stat.value}</div>
                  <div className="text-sm text-zinc-400">{stat.label}</div>
                  {stat.subtext && <div className="text-xs text-zinc-500">{stat.subtext}</div>}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </Link>
  );
};
